import type { HelpSection } from './helpContent.types'

export const helpContent: HelpSection[] = [
  {
    title: 'General Program Manager Questions',
    subSections: [
      {
        entries: [
          {
            question: 'What is the Setup?',
            answer:
              'The Setup tab is a series of steps a Program Manager needs to take to get the Sidekick Platform up and running for their End Users.',
          },
          {
            question: 'How do I get help during the configuration process? ',
            answer:
              'Log in to the app on your device. The Personal Sidekick can help guide you through all the steps of the setup process with all the detail you need. Press the \u201cTap to Talk\u201d button and begin asking your question, ask your question and then tap again when you are done or type your question in the message box and send it to your Sidekick.',
          },
          {
            question: 'What are some important terms I should know for setup and configuration?',
            answer:
              'There are some important terms you should know when configuring and working with the Portal: \n 1. Expert Sidekicks: Expert Sidekicks (ES) are AI agents that guide End Users through their area of expertise, using information provided by your Experts.​ \n 2. Personal Sidekick: Personal Sidekicks are AI agents that guide and support End Users \n 3. User Profile: The User Profile has fields that the End User can populate in their app to give their Sidekicks details to personalize their experience. \n 4. Experts: Experts are selected by your organization to build the Content used by Expert Sidekicks to support End Users.\n 5. Sidekick Terms: Sidekick Terms customize the words Sidekicks use in the app to make the app feel just right for your organization. \n 6. Expert Content Modules: materials created by Experts about the topics they are knowledgeable, and presented by Expert Sidekicks.​ ',
          },
          {
            question: 'What is involved in Setup?',
            answer:
              'The setup process is a few steps you need to take before you start getting users on the system. There are a few major steps: \n 1. Download the Sidekick App \n 2.  Define the Topics for Expert Sidekicks  \n 3. Invite Experts. \n 4. Draft Welcome Message \n 5. Setup Complete! Ready for Content',
          },
          {
            question: 'Do I have to do all the steps in Setup?',
            answer:
              'No. You do not need to invite Experts. You can act as a Expert and create content by yourself.',
          },
          {
            question: 'What about after I complete the setup?',
            answer:
              'After Setup, you can make changes in the Settings. These settings are: \n 1. Define Topics for Sidekicks \n 2. Select the available Personal Sidekicks \n 3. Define fields in the user profile \n 4. Customize the terms used by the Sidekick',
          },
        ],
      },
    ],
  },
  {
    title: 'Configuring Sidekick',
    subSections: [
      {
        title: 'Step 1: Downloading the Application',
        entries: [
          {
            question: 'Why do I need to download the application? ',
            answer:
              'There are a couple reasons to download the application. The Personal Sidekick can help walk you through all the steps in the Configuration Process as you are working through it. It is also important to test Content in the Sidekick Platform app to ensure that everything is working how you expect it to. ',
          },
          {
            question: 'What platforms are supported for the app?',
            answer: 'The app is available on iOS and Android.',
          },
        ],
      },
      {
        title: 'Step 2: Define Topics for Sidekicks',
        entries: [
          {
            question: 'What do I need to do to Define Topics for Sidekicks?',
            answer:
              'In this step, you define Topics--or the area of expertise for each Sidekick. This Topic will have a Sidekick automatically assigned.',
          },
          {
            question: 'Why do I need to Define Topics for Sidekicks?',
            answer:
              'Each Topic gets a unique Sidekick to represent the Experts content. Topics are also used to categorize your content.',
          },
          {
            question: 'Can I change the Sidekick for a Topic?',
            answer:
              'Yes! To change the Sidekick, click the pencil icon. Then, select a new Sidekick from the gallery. (You will be unable to select Sidekicks that are already in use)',
          },
          {
            question: 'Does the order of the Topics list matter?',
            answer:
              'The order of the topics list matters. The system prioritizes the Topics in the order they are displayed. The Sidekicks will be more likely to offer the End User topics that are higher up in the list more frequently. To change the order of the topics, either press and hold the primary mouse button  on the six dots and drag a topic to your desired location OR click on the six dots and select move up or move down to move by one.',
          },
          {
            question: 'What is the difference between a Topic and a Sub-Topic?',
            answer:
              'A Topic is the major category for information and will be used to organize content in the Content Library. A Sub-Topic helps the Sidekicks provide more relevant content to the End User.',
          },
          {
            question: 'What is an Expert Sidekick?',
            answer:
              'An Expert Sidekick is an AI Agent created to guide your End Users through specialized information from Experts. Topics are areas of expertise (like health, education, or finance) for the Expert Sidekicks that End Users will interact with.',
          },
          {
            question: 'Can I have multiple Sub-Topics with the same name?',
            answer:
              'Not in the same Topic. However, different Topics can have Sub-Topics with the same name. For example, the Healthcare Topic could have a \u201cGeneral\u201d Sub-Topic, and the Education Topic could also have its own \u201cGeneral\u201d Sub-Topic.  However, these are NOT the same.',
          },
          {
            question: 'Can I add more Topics?',
            answer: 'Yes. Click the \u201cAdd Topic\u201d button above the Topic list.',
          },
          {
            question: 'Can I add more Sub-Topics?',
            answer: 'Yes. Click the plus sign to add an additional Sub-Topic.',
          },
          {
            question: 'Can I remove all the Sub-Topics?',
            answer:
              'Yes, you can. We recommend you use Subtopics in preparation for later updates, where Expert Sidekicks will use the  Subtopics to further personalize the content they deliver to the End Users.',
          },
          {
            question: 'How can I change the name of the Topics that are already there?',
            answer:
              'If a Topic does not have any content assigned to it, you can change the name of the Topic by clicking directly on the text. You cannot change the name of a Topic if there is content assigned to it.',
          },
          {
            question: 'Can I change the name of the Sub-Topic?',
            answer:
              'You can change the name of the Sub-Topic by clicking on it, typing a new name, and then clicking outside of it.',
          },
          {
            question: 'Can I delete a Topic?',
            answer:
              'You can delete a Topic if there are no content modules associated with it. Delete the Topic by clicking on the 6 dots at the left of the Topic and select \u201cDelete\u201d from the menu that pops up.',
          },
          {
            question: 'Why didn\u2019t my changes apply?',
            answer:
              'To apply your changes, you need to save them using the \u201cSave\u201d button in the upper righthand corner of the screen.',
          },
          {
            question: 'Why are the \u201cSave\u201d and \u201cCancel\u201d buttons greyed out? ',
            answer:
              'The \u201cSave\u201d and \u201cCancel\u201d button will only become available when you make changes to the page. Click \u201cSave\u201d to keep your changes and \u201cCancel\u201d to discard them.',
          },
        ],
      },
      {
        title: 'Step 3: Invite Experts',
        entries: [
          {
            question: 'What is a Expert?',
            answer:
              'Experts are selected by your organization to build the Content used by Expert Sidekicks to support End Users. Add Experts using their name and email address and then assign them a Topic.',
          },
          {
            question: 'What is the difference between a Expert and a Program Manager?',
            answer:
              'Experts have fewer privileges than a Program Manager. When a Expert creates Content, they must have a Program Manager approve it before the End Users can see it. Experts also cannot access the Setup or Settings tabs.',
          },
          {
            question: 'Do I need Experts?',
            answer:
              'Many Customers have Experts to help create Content. However, some Customers may only have a single Program Manager who does everything.',
          },
          {
            question: 'What does it mean to assign the Experts Topics?',
            answer: 'Experts can only create Content within their assigned Topic.',
          },
          {
            question: 'Can I give a Expert more than one Topic?',
            answer:
              'Yes, you can give a Expert more than one Topic and they will be able to create Content for any of those Topics.',
          },
          {
            question: 'Can I add another Program Manager?',
            answer:
              'Not at the moment. If you need additional Program Managers for your Sponsor, please contact support@sidekickwellness.com.',
          },
        ],
      },
      {
        title: 'Step 4: Setup Complete! Ready for Content',
        subSections: [
          {
            title: 'Next Steps',
            entries: [
              {
                question: 'Setup is complete. Now what?',
                answer:
                  'You are now ready to get content on the system! There are a few things you should do: \n 1. Complete the \u201cAbout Us: General Information\u201d Content Module with information about your organization. \n 2. If you have Experts, have them submit Content Modules for you to approve. Then review and approve them. \n 3. Add yourself as an End User to test the full experience \n 4. When ready, begin inviting users.',
              },
              {
                question: 'Where do I invite myself as an End User?',
                answer:
                  'You can invite yourself as an End User from the People menu item under Invite End User. You will need a second email address you have access to.',
              },
            ],
          },
          {
            title: 'Complete the \u201cAbout Us: General Information\u201d Content Module ',
            entries: [
              {
                question: 'Where do I find the \u201cAbout Us: General Information\u201d Content Module?',
                answer: 'The \u201cAbout Us: General Information\u201d Content Module is found in the Content Library.',
              },
              {
                question: 'What is the \u201cAbout Us: General Information\u201d Content Module?',
                answer:
                  'The \u201cAbout Us: General Information\u201d Content Module controls the way the Personal Sidekick interacts with the user in the app, and what the Personal Sidekick knows about your organization.',
              },
              {
                question: 'What is the \u201cAbout Us: General Information\u201d Content Module called?',
                answer:
                  'It is called \u201cAbout Us: General Information\u201d when you first start Setup. You can rename it to whatever suits you.',
              },
              {
                question:
                  'Who is responsible for filling out the \u201cAbout Us: General Information\u201d Content Module?',
                answer:
                  'The \u201cAbout Us: General Information\u201d Content Module is maintained by the Program Manager',
              },
              {
                question: 'Why can\u2019t I edit the \u201cAbout Us: General Information\u201d Content Module?',
                answer:
                  'The \u201cAbout Us: General Information\u201d Content Module must be put into Draft mode before it can be edited. To do this, click the Published status and then select Back to Draft.',
              },
              {
                question:
                  'What kind of information should I put in the \u201cAbout Us: General Information\u201d Content Module?',
                answer:
                  'The \u201cAbout Us: General Information\u201d Content Module should contain information that you would like your users to know about your organization. This includes things like your history, mission, important resources, and anything else you can think of.',
              },
              {
                question: 'How do I get to the \u201cAbout Us: General Information\u201d Content Module?',
                answer:
                  'Go to the Content Library. On the All Modules screen, look for the \u201cAbout Us: General Information\u201d module.',
              },
              {
                question: 'How do I edit the \u201cAbout Us: General Information\u201d Content Module?',
                answer:
                  'Click the pencil icon to begin editing. Make changes, add content as needed, and then save them.',
              },
              {
                question: 'How do I get the \u201cAbout Us: General Information\u201d Content Module into the app?',
                answer:
                  'Once you\u2019re satisfied with the content in the \u201cAbout Us: General Information\u201d Content Module, click the Status. Then, select Publish.',
              },
              {
                question: 'I can\u2019t edit my Content Module! What do I do?',
                answer:
                  'Content modules are only editable in Draft mode. To put the \u201cAbout Us: General Information\u201d Content Module back into Draft Mode, click the Published status, and then select back to draft.',
              },
            ],
          },
          {
            title: 'Review and Publish Expert Content Modules',
            entries: [
              {
                question: 'Where would I find the Content Modules created by my Experts?',
                answer:
                  'Content Modules created by your Experts will be available in the Awaiting Approval tab once they have been submitted for review.',
              },
              {
                question: 'Why do I need to Review Expert Content Modules?',
                answer:
                  'As a Program Manager, you should review your Expert\u2019s Content Modules before approving them to ensure completeness and accuracy of content.',
              },
              {
                question: 'Why do I need to Publish the content my Experts create?',
                answer:
                  'As the Program Manager, you are able to control the content your End Users see. You approve content after the Experts submit it to you.',
              },
              {
                question: 'Why do I NOT need to approve content I created?',
                answer:
                  'As a Program Manager, you can publish your content directly, although we do recommend testing it beforehand.',
              },
              {
                question: 'What if it\u2019s just me and no Experts?',
                answer:
                  'If you don\u2019t have any Experts and you are the only one creating content, you can ignore this step. You can publish your content directly (after testing, of course). We have this step here so our Program Managers who have invited Experts know that approving content is an important step in the configuration process.',
              },
            ],
          },
        ],
      },
    ],
  },
  {
    title: 'Settings',
    subSections: [
      {
        title: 'Topics',
        entries: [
          {
            question: 'What do I need to do to Define Topics for Sidekicks?',
            answer:
              'In this step, you define Topics--or the area of expertise for each Sidekick. This Topic will have a Sidekick automatically assigned.',
          },
          {
            question: 'Why do I need to Define Topics for Sidekicks?',
            answer:
              'Each Topic gets a unique Sidekick to represent the Experts content. Topics are also used to categorize your content.',
          },
          {
            question: 'Can I change the Sidekick for a Topic?',
            answer:
              'Yes! To change the Sidekick, click the pencil icon. Then, select a new Sidekick from the gallery. (You will be unable to select Sidekicks that are already in use)',
          },
          {
            question: 'Does the order of the Topics list matter?',
            answer:
              'The order of the topics list matters. Higher ranked topics are suggested first. To change the order of the topics, either press and hold the primary mouse button  on the six dots and drag a topic to your desired location OR click on the six dots and select move up or move down to move by one.',
          },
          {
            question: 'What is the difference between a Topic and a Sub-Topic?',
            answer:
              'A Topic is the major category for information and will be used to organize content in the Content Library. A Sub-Topic helps the Sidekicks provide more relevant content to the End User.',
          },
          {
            question: 'What is an Expert Sidekick?',
            answer:
              'An Expert Sidekick is an AI Agent created to guide your End Users through specialized information from Experts. Topics are areas of expertise (like health, education, or finance) for the Expert Sidekicks that End Users will interact with.',
          },
          {
            question: 'Can I have multiple Sub-Topics with the same name?',
            answer:
              'Not in the same Topic. However, different Topics can have Sub-Topics with the same name. For example, the Healthcare Topic could have a \u201cGeneral\u201d Sub-Topic, and the Education Topic could also have its own \u201cGeneral\u201d Sub-Topic.  However, these are NOT the same.',
          },
          {
            question: 'Can I add more Topics?',
            answer: 'Yes. Click the \u201cAdd Topic\u201d button above the Topic list.',
          },
          {
            question: 'Can I add more Sub-Topics?',
            answer: 'Yes. Click the plus sign to add an additional Sub-Topic.',
          },
          {
            question: 'Can I remove all the Sub-Topics?',
            answer:
              'Yes, you can. We recommend you use Subtopics in preparation for later updates, where Expert Sidekicks will use the  Subtopics to further personalize the content they deliver to the End Users.',
          },
          {
            question: 'How can I change the name of the Topics that are already there?',
            answer:
              'If a Topic does not have any content assigned to it, you can change the name of the Topic by clicking directly on the text. You cannot change the name of a Topic if there is content assigned to it.',
          },
          {
            question: 'Can I change the name of the Sub-Topic?',
            answer:
              'You can change the name of the Sub-Topic by clicking on it, typing a new name, and then clicking outside of it.',
          },
          {
            question: 'Can I delete a Topic?',
            answer:
              'You can delete a Topic if there are no content modules associated with it. Delete the Topic by clicking on the 6 dots at the left of the Topic and select \u201cDelete\u201d from the menu that pops up.',
          },
          {
            question: 'Why didn\u2019t my changes apply?',
            answer:
              'To apply your changes, you need to save them using the \u201cSave\u201d button in the upper righthand corner of the screen.',
          },
          {
            question: 'Why are the \u201cSave\u201d and \u201cCancel\u201d buttons greyed out? ',
            answer:
              'The \u201cSave\u201d and \u201cCancel\u201d button will only become available when you make changes to the page. Click \u201cSave\u201d to keep your changes and \u201cCancel\u201d to discard them.',
          },
        ],
      },
      {
        title: 'Select Personal Sidekicks',
        entries: [
          {
            question: 'What do I need to do in Select Personal Sidekick?',
            answer: 'Select the Personal Sidekick options for the End Users to choose from.',
          },
          {
            question: 'What is a Personal Sidekick?',
            answer: 'Personal Sidekicks are AI Agents created to guide your End Users through their experience.',
          },
          {
            question: 'Why do you recommend 6 Personal Sidekicks?',
            answer:
              'More Sidekicks give the End Users more chance to find a Sidekick they relate to. You should offer a minimum of two.',
          },
          {
            question: 'What are the Sidekicks with the dots?',
            answer:
              'The Sidekicks with the dot images provide the user a chance to pick a non-human image while still enabling them to visualize audio coming into the app',
          },
          {
            question: 'What does the purple star mean?',
            answer: 'The Sidekick with the purple star is the default Personal Sidekick.',
          },
          {
            question: 'What is the purpose of the default Personal Sidekick?',
            answer:
              'The default Personal Sidekick (with the purple star) is the Sidekick the app will use the first time the user enters the app.',
          },
          {
            question: 'What do I need to do to select Personal Sidekicks? ',
            answer:
              '1. First, if you have 6 Sidekicks selected, click on an already selected Sidekick to de-select them. \n 2. Next, select another Sidekick \n 3. When you are satisfied with your selections, click Save',
          },
        ],
      },
      {
        title: 'Design the User Profile',
        entries: [
          {
            question: 'Why is it important to Design the User Profile?',
            answer:
              'The User Profile has fields that the End User can populate in their app to give their Sidekicks details to personalize their experience. User Profiles help Sidekicks personalize and prioritize content for specific End Users. You get to choose what the most important and useful information for the Profile.',
          },
          {
            question: 'Where are these fields coming from?',
            answer:
              'These fields were created by Sidekick to cover most of the information we think Organizations might need to help the Expert Sidekicks get the most relevant information.',
          },
          {
            question: 'What if I don\u2019t see the fields I need?',
            answer:
              'Please email support@sidekickwellness.com with the name of the field, the description of the field, and how it should be used. We can add it for you.',
          },
          {
            question: 'Can I change the order of the fields?',
            answer:
              'Yes, you can drag the fields up or down by clicking on the six dots and moving the item up or down within its group. You can also rearrange whole groups. Make sure to save after you are done.',
          },
        ],
      },
      {
        title: 'Define Sidekick Terms',
        entries: [
          {
            question: 'Why is it important to define Sidekick Terms?',
            answer:
              'Sidekick Terms customize the words Sidekicks use in the app to make the app feel just right for your organization. Sidekicks use this description of your organization to speak to End Users with accurate details and the right tone.',
          },
          {
            question: 'What is the difference between the Customer Name field and the Customer Short Name field?',
            answer:
              'The Customer Name is the full name of your organization, while the short name is an abbreviation (if you have one). For example, the Customer Name might be \u201cCollege University\u201d and the Customer Short Name might be \u201cCU.\u201d',
          },
          {
            question: 'What is the End User Role field?',
            answer:
              'The End User Role is what you\u2019d like to call the people who are the intended audience\u2014or the people who will be using the app to access your content and resources. Examples include student, parent, veteran, employee, or user.',
          },
        ],
      },
      {
        title: 'People',
        subSections: [
          {
            title: 'Experts',
            entries: [
              {
                question: 'What is a Expert?',
                answer:
                  'Experts are selected by your organization to build the Content used by Expert Sidekicks to support End Users. Add Experts using their name and email address and then assign them a Topic.',
              },
              {
                question: 'What is the difference between a Expert and a Program Manager',
                answer:
                  'Experts have fewer privileges than a Program Manager. When a Expert creates Content, they must have a Program Manager approve it before the End Users can see it. Experts also cannot access the Setup or Settings tabs.',
              },
              {
                question: 'Do I need Experts?',
                answer:
                  'Many Customers have Experts to help create Content. However, some Customers may only have a single Program Manager who does everything.',
              },
              {
                question: 'What does it mean to assign the Experts Topics?',
                answer: 'Experts can only create Content within their assigned Topic.',
              },
              {
                question: 'Can I give a Expert more than one Topic?',
                answer:
                  'Yes, you can give a Expert more than one Topic and they will be able to create Content for any of those Topics.',
              },
              {
                question: 'Can I add another Program Manager?',
                answer:
                  'Not at the moment. If you need additional Program Managers for your Sponsor, please contact support@sidekickwellness.com.',
              },
            ],
          },
          {
            title: 'End User',
            entries: [
              {
                question: 'Do I need to invite myself or my Experts as End Users?',
                answer:
                  ' You can experience most content by logging in to the app with your app credentials. However, if you want to have a true user experience, then you should invite another email address you have as an End User.',
              },
            ],
          },
        ],
      },
    ],
  },
  {
    title: 'General Expert Questions',
    subSections: [
      {
        entries: [
          {
            question: 'What is the Content Portal?',
            answer:
              'The Content Portal is an interface that allows Experts to design Content Modules and distribute them to the End Users of their organization. It also allows them to see metrics about how their Content Modules are being used.',
          },
          {
            question: 'What is a Content Module?',
            answer:
              'A Content Module is a collection of materials about a subject that are stored together to be presented to the End Users.',
          },
          {
            question: 'What is an Expert Sidekick?',
            answer:
              'An Expert Sidekick is knowledgeable about specific topics as defined by a Expert. Their goal is to help the End User gain knowledge in the Expert’s field.',
          },
          {
            question: 'What is a Personal Sidekick?',
            answer:
              'A Personal Sidekick is the End User’s best friend, confidant, concierge, all in their pocket. They help the End User figure out what they need to do, and who they need to speak to so they can get their task done.',
          },
          {
            question: 'How do I create a new Content Module?',
            answer: 'On the Content Module screen on the right side of the screen, click the New Module button.',
          },
          {
            question: 'How do I create Topics?',
            answer: 'Topics and Subtopics are created by the Program Manager.',
          },
        ],
      },
    ],
  },
  {
    title: 'Creating a Content Module',
    subSections: [
      {
        title: 'Step 1: Select a Topic',
        entries: [
          {
            question: 'What does the Topic do?',
            answer: 'The Topic is the main heading in the Content Library listing.',
          },
          {
            question: 'What does the Subtopic do?',
            answer:
              'The Subtopic is used as a tag, and helps the Personal Sidekick determine which Content is most relevant for the End User.',
          },
          {
            question: 'Can I select the Expert Sidekick  for my content?',
            answer: 'Not at the moment. The Expert Sidekicks are set at the topic level.',
          },
        ],
      },
      {
        title: 'Step 2: Add Content',
        entries: [
          {
            question: 'What is File Content?',
            answer: 'File content is the information the Expert Sidekick accesses to give to the End User.',
          },
          {
            question: 'How many files can I have?',
            answer:
              'You may have one or many files. We start you off with a single file, but you can upload more after. Currently, End Users can ask about content in any file in the Content Module at any point.',
          },
          {
            question: 'How do I add my material as Content?',
            answer: 'You can upload a file or copy-paste plain text into the box.',
          },
          {
            question: 'What file formats can I upload?',
            answer:
              'The file formats for the main content are: .txt, .docx, and .pdf. Supplemental image formats are: .png, .jpeg, .gif.',
          },
          {
            question: 'What is the deal with the Files inside each Content Module?',
            answer: 'Files enable you to create a structured experience for End Users.',
          },
          {
            question: 'Why do I need FAQs?',
            answer:
              'FAQs are helpful for End Users and Sidekicks. End Users can ask for FAQs if they aren’t sure what to ask. Sidekicks have easy access to some of the most common questions so they can answer them quickly. For example, "How do I reset my password?" or "Where do I go to talk with someone about my benefits?"',
          },
          {
            question: 'Do I have to accept all the FAQs the Sidekick generates?',
            answer:
              'No, they are suggestions. You can accept some or all, and edit any questions and answers that aren’t quite right.',
          },
          {
            question: 'What are Supplemental Resources and why are they important?',
            answer:
              'You can add Supplemental Resources—like links, images, or videos—to extend your content. These Resources will be shown by the Expert Sidekick when relevant in the context of a conversation.',
          },
          {
            question: 'What are some best practices for uploading Supplemental Resources?',
            answer:
              'Specify in the Description what type of Supplemental Resource you are posting (is it a video? Link to website? Screening tool?)\n- Say in the Description when you want the Expert Sidekick to provide the material. For example, "If user asks about X, then show this video."\n- Add more information into the Description.',
          },
          {
            question: 'What if I want to add more files?',
            answer:
              'To add more files after the initial file, click the “Add another file, and then follow the prompts again.',
          },
        ],
      },
      {
        title: 'Step 3: Describe the Module',
        entries: [
          {
            question: 'Why do we have to describe the Content Module? What do all these fields do?',
            answer:
              'Describing the Content Module accurately helps the Sidekicks bring up the relevant information at the right time in the conversation with the End User.',
          },
          {
            question: 'Can I change what the AI suggests for each field?',
            answer:
              'Yes. You can edit the Sidekick’s suggestions simply by clicking in the field and entering new text.',
          },
          {
            question: 'What makes for a good Title?',
            answer:
              'The title should be descriptive enough so the End User can get the general idea of what the Content Module is about. The End User will see the Title of your Content Module in a page called “Content Library” in the Sidekick Platform app.',
          },
          {
            question: 'What is the Module Description?',
            answer:
              'The Module Description field is what the Personal Sidekick knows about the Content Module and it will use it to describe the Content Module to End Users.',
          },
          {
            question: 'What is the Target Audience?',
            answer:
              'The “Target Audience” field is your opportunity to explain who the primary audience for your Content Module is. Your audience could be very general (“Everyone”) to very specific (“people over the age of 35 who are interested in becoming welders”). You can describe your audience in whatever way that makes sense to you. Then the Personal Sidekicks will use this information in combination with what they know about the individual End User to personalize their guidance to them.',
          },
          {
            question: 'What are Module Objectives?',
            answer:
              'Module objectives are an incredibly powerful feature. Essentially, the Module Objectives are what you want the Expert Sidekick to accomplish as they are going through your Content Module.',
          },
          {
            question: 'How do I get the most out of Module Objectives?',
            answer:
              'The Module Objectives are especially powerful when they tell the Expert Sidekick about things they should do relative to Content included in that Content Module.\n\nSome ideas for using Module Objectives well:\n- Provide an outline or list of the topics you want the Sidekick to cover\n- Number the Module Objectives (the Expert Sidekicks will execute them in order)\n- Refer to the Content within the Content Module by Name (Files, Website, Youtube Video, Image, etc)\n- Keep Module Objectives brief\n\nAdditional things you can do with Module Objectives:\n- Provide additional instructions in if-then format\n- Give limited behavioral instructions\n- Suggest when to offer specific Supplemental Resources (by name)\n\nThese customized Module Objectives for the Expert Sidekick will work alongside a set of generic instructions that the Expert Sidekick already follows, particularly general behavior instructions like being professional yet kind, keeping responses relatively short, and only answering from information it has.',
          },
          {
            question: 'Can you give me some examples of good Module Objectives?',
            answer:
              '**Example 2**\n\n1. When someone inquires about a topic, offer to show them a relevant video.\n2. If a screening tool or self-assessment is available in supplementary materials or in your memory, always offer it to the user.\n3. When describing a new topic, ask the user if it sounds like something that might be affecting them.\n4. When asked about Traumatic Brain Injury (TBI), offer the supplementary video, "Video:  Coping with TBI to improve quality of life."\n5. Always ask questions in addition to providing information.\n6. If user asks about X, then provide them with the following 3 steps\n    1. __\n    2. __\n    3. __',
          },
          {
            question: 'What is the Welcome Message?',
            answer:
              'The Welcome Message is the first message the End User will receive when they enter into the Content Module. The Expert Sidekick paraphrases the Welcome Message, but it will cover all the main points in your introduction.\n\n**Best Practices for Welcome Message:**\n- Make clear what your content is about\n- End with a leading question\n- Keep it to a couple of sentences',
          },
        ],
      },
      {
        title: 'Step 4: Save Module',
        entries: [
          {
            question: 'Will my Content Module become available to End Users when I save it?',
            answer:
              'No. Saving your Content Module will not make it available to End User. Your content will be available in the app after you send it to the Program Manager for review and they publish it. ',
          },
        ],
      },
    ],
  },
  {
    title: 'Managing a Content Module',
    subSections: [
      {
        entries: [
          {
            question: 'I’ve created a Content Module, but it says Draft. What do I do now?',
            answer:
              'First put your Content Module into Testing and use the app to make sure you are happy with the content. Then, change the Status to Submit for Approval so your Program Manager can publish it to the application.',
            roleBasedAnswers: {
              'Expert':
                'First put your Content Module into Testing and use the app to make sure you are happy with the content. Then, change the Status to Submit for Approval so your Program Manager can publish it to the application.',
              'Program Manager':
                'First put your Content Module into Testing and use the app to make sure you are happy with the content. Then, change the Status to Publish to make it available to End Users in the application.',
            },
          },
          {
            question: 'Where do I change the status for a Content Module?',
            answer: 'You can click on the status to bring up the available statuses you can switch to. ',
          },
          {
            question: 'Why can’t I edit all the Content Modules?',
            answer: 'Content Module editing is restricted to the owner of the Content Module.',
          },
          {
            question: 'Can I edit a Content Module?',
            answer:
              'You can edit content that is in draft mode.\n1. If your Content Module is in Published status, first ask your Program Manager to unpublish it. \n2. On the Modules screen, to the left of the Content Module, click the pencil icon to get into Edit mode.\n3. Modify your content as you see fit.\n4. When ready, click Submit for Approval. Your content will be sent to the Program Manager for approval.',
            roleBasedAnswers: {
              'Expert':
                'You can edit content that is in draft mode.\n1. If your Content Module is in Published status, first ask your Program Manager to unpublish it. \n2. On the Modules screen, to the left of the Content Module, click the pencil icon to get into Edit mode.\n3. Modify your content as you see fit.\n4. When ready, click Submit for Approval. Your content will be sent to the Program Manager for approval.',
              'Program Manager':
                'You can edit content that is in draft mode.\n1. If your Content Module is in Published status, first unpublish it. \n2. On the Modules screen, to the left of the Content Module, click the pencil icon to get into Edit mode.\n3. Modify your content as you see fit.\n4. After saving edits, click Publish to make it available to End Users in the application.',
            },
          },
          {
            question: 'Can I see Content Modules other Experts have created?',
            answer: 'Yes, you can view the content in a view-only mode.',
          },
          {
            question: 'Can I delete a Content Module?',
            answer:
              'Yes, you can delete a Content Module you own. However, please note your content will be deleted immediately, even if there are users interacting with the content.\n\nTo delete a Content Module:\n1. If your Content Module is in Published status, first ask your Program Manager to unpublish it.\n2. On the Content Module Screen, at the right side of the Content Module, click the status.\n3. From the dropdown list, click Delete. Your Content Module will be deleted.',
            roleBasedAnswers: {
              'Expert':
                'Yes, you can delete a Content Module you own. However, please note your content will be deleted immediately, even if there are users interacting with the content.\n\nTo delete a Content Module:\n1. If your Content Module is in Published status, first ask your Program Manager to unpublish it.\n2. On the Content Module Screen, at the right side of the Content Module, click the status.\n3. From the dropdown list, click Delete. Your Content Module will be deleted.',
              'Program Manager':
                'Yes, you can delete a Content Module you own. However, please note your content will be deleted immediately, even if there are users interacting with the content.\n\nTo delete a Content Module:\n1. If your Content Module is in Published status, first unpublish it.\n2. On the Content Module Screen, at the right side of the Content Module, click the status.\n3. From the dropdown list, click Delete. Your Content Module will be deleted.',
            },
          },
          {
            question: 'Why is my Content Module not saving?',
            answer:
              'Your Content Module may not be saving for several reasons. There may be empty fields your need to fill out or you may need to accept an AI option.\n\nTo accept an AI Option:\n- Click the Accept button at the top right above the AI-generated content box (indicated with a gradient background) OR\n- Click in to the field, make any desired edits,  and then click outside the field.',
          },
          {
            question: 'Can I edit a Content Module?',
            answer:
              'You can absolutely go back and Edit your content, as long as it is not Published. To Edit the content:\n1. On the Modules screen, to the left of the Content Module, click the pencil icon to get into Edit mode.\n2. Modify your content as you see fit.\n3. When ready, click Save Module.',
          },
        ],
      },
    ],
  },
  {
    title: 'Prioritizing Modules ',
    subSections: [
      {
        entries: [
          {
            question: 'What does prioritizing Modules do?',
            answer:
              'Prioritizing Content Modules changes the order that they are suggested for the End User in the Content Library. The Content Modules are ranked with #1 as the most important.',
          },
          {
            question: 'How do I prioritize Modules?',
            answer:
              'If you have 2 or more Content Modules assigned to you, you can simply drag and drop to change their rank ordering, with rank 1 being the highest priority.\n\nTo Prioritize Modules:\n1. Navigate to the Module Prioritization tab\n2. Press and hold the mouse on the dots on the left of the Content Module\n3. Drag the Module higher or lower as desired',
          },
        ],
      },
    ],
  },
  {
    title: 'Metrics',
    subSections: [
      {
        entries: [
          {
            question: 'How do I get to the Metrics?',
            answer: 'Click the Metrics button in the lefthand navigation panel.',
          },
          {
            question: 'What is shown on the Overview Tab?',
            answer:
              '1. General Statistics (for the last Month)\n\t- Number of Content Modules\n\t- Total number of conversations for all Content Modules\n\t- Number of messages sent\n\t- Number of Unique Conversations\n2. Daily Active End Users Graph\n3. CM Conversations Graph: Total and Unique conversations.',
          },
          {
            question: 'What is shown on the Topics Tab?',
            answer:
              'The Topics tab provides metrics on the performance of individual topics for the last month.\n1. Per-topic statistics\n\t- Unique visitors\n\t- Total Number of Conversations for the specific conversation\n\t- End User Messages: total number of messages sent by End Users\n\t- Average length: average number of messages in a conversation\n\t- Max length: Number of messages in the longest conversation\n2. Graph of Number of Conversations over Time:\n\t- Bar chart representing the percentage of all conversations with all Topics for a given day\n\t- Line graph representing number of conversations with a Topic for a given day\n\t- Line graph representing number of unique End Users  interacting with a Topic for a given day',
          },
          {
            question: 'What is shown in the Content Modules Tab?',
            answer:
              'The Content Modules tab provides metrics of individual Content Modules in the last month.\n1. Per-topic statistics:\n\t1. Unique visitors\n\t2. Total Number of Conversations for the specific conversation\n\t3. End User Messages: total number of messages sent by End Users\n\t4. Average length: average number of messages in a conversation.\n\t5. Max length: Number of messages in the longest conversation\n2. Graph of Number of Conversations over Time:\n\t1. Bar chart representing the percentage of all conversations with a Content Module for a given day\n\t2. Line graph representing number of conversations with a given Content Module for a given day\n\t3. Line graph representing number of unique End Users interacting with a Content Module for a given day',
          },
        ],
      },
    ],
  },
]
